import React from 'react';
import { useAtom } from 'jotai';
import { formProgressAtom } from '../store/form';
import { paymentMethods, maritalStatusOptions } from '../types/form';
import { FileText, User, MapPin, Zap, DollarSign, Loader2, Ligature as FileSignature, ArrowLeft, ExternalLink, Save, CheckCircle, Lock, Unlock, FileDown, Users } from 'lucide-react';
import { generateContract } from '../utils/generateContract';
import { generateContractWord } from '../utils/generateContractWord';
import { generateDualContract } from '../utils/generateDualContract';
import { generatePowerOfAttorney } from '../utils/generatePowerOfAttorney';
import { SecondContractor } from '../types/form';
import { useSupabaseMutation } from '../hooks/useSupabaseMutation';
import ConfirmationModal from './ConfirmationModal';

export default function ReviewForm() {
  const [formProgress, setFormProgress] = useAtom(formProgressAtom);
  const [isGeneratingContract, setIsGeneratingContract] = React.useState(false);
  const [isGeneratingPowerOfAttorney, setIsGeneratingPowerOfAttorney] = React.useState(false);
  const [estadoLogin, setEstadoLogin] = React.useState(false);
  const [isAutomating, setIsAutomating] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);
  const [showUnlockModal, setShowUnlockModal] = React.useState(false);
  const [showDualContract, setShowDualContract] = React.useState(false);
  const [isGeneratingDual, setIsGeneratingDual] = React.useState(false);
  const [secondContractor, setSecondContractor] = React.useState<SecondContractor>({
    fullName: 'IRIANA LOPES DE CASTRO FAIDIGA',
    cpf: '080.429.738-02',
    rg: '148209178',
    issuingAuthority: 'SESP/SP',
    nationality: 'BRASILEIRA',
    maritalStatus: 'Married',
    email: 'IRIANAFAIDIGA@YAHOO.COM.BR',
    sameAddress: true
  });
  const { mutateAsync: savePowerOfAttorney } = useSupabaseMutation('power_of_attorney');

  const handleUnlockContract = () => {
    setFormProgress(prev => ({
      ...prev,
      isLocked: false
    }));
  };

  const handleSincetClick = () => {
    if (!estadoLogin) {
      alert("Faça login no sistema que será aberto em uma nova aba. Depois volte nesta aba e clique novamente no botão para iniciar o preenchimento automático.");
      window.open("https://servicos.sinceti.net.br/", "_blank");
      setEstadoLogin(true);
    } else {
      alert("Pressione as teclas Alt + L para ativar o fluxo e preencher automaticamente os dados no Sincet.");
      setEstadoLogin(false);
    }
  };

  const handleGenerateContract = async () => {
    try {
      setIsGeneratingContract(true);
      
      // First, save the power of attorney to the database
      await savePowerOfAttorney({
        full_name: formProgress.data.customerInfo?.fullName,
        cpf: formProgress.data.customerInfo?.cpf,
        rg: formProgress.data.customerInfo?.rg,
        issuing_authority: formProgress.data.customerInfo?.issuingAuthority,
        street: formProgress.data.installationLocation?.street,
        number: formProgress.data.installationLocation?.number,
        neighborhood: formProgress.data.installationLocation?.neighborhood,
        city: formProgress.data.installationLocation?.city,
        state: formProgress.data.installationLocation?.state
      });
      
      // Generate both contract and power of attorney
      const [contractBlob, powerOfAttorneyBlob] = await Promise.all([
        generateContract(formProgress.data),
        generatePowerOfAttorney(formProgress.data)
      ]);
      
      // Download contract
      const url = window.URL.createObjectURL(contractBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contrato-sistema-fotovoltaico.pdf');
      document.body.appendChild(link);
      link.click();
      
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Download power of attorney
      const powerOfAttorneyUrl = window.URL.createObjectURL(powerOfAttorneyBlob);
      const powerOfAttorneyLink = document.createElement('a');
      powerOfAttorneyLink.href = powerOfAttorneyUrl;
      powerOfAttorneyLink.setAttribute('download', 'procuracao-sistema-fotovoltaico.pdf');
      document.body.appendChild(powerOfAttorneyLink);
      powerOfAttorneyLink.click();
      
      powerOfAttorneyLink.parentNode?.removeChild(powerOfAttorneyLink);
      window.URL.revokeObjectURL(powerOfAttorneyUrl);
      
    } catch (error) {
      console.error('Error generating contract:', error);
      alert('Erro ao gerar documentos. Por favor, tente novamente.');
    } finally {
      setIsGeneratingContract(false);
    }
  };

  const handleGenerateDualContract = async () => {
    if (!secondContractor.fullName || !secondContractor.cpf || !secondContractor.rg) {
      alert('Por favor, preencha pelo menos nome, CPF e RG do segundo contratante.');
      return;
    }
    try {
      setIsGeneratingDual(true);
      const blob = await generateDualContract(formProgress.data, secondContractor);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato-2-contratantes-${(formProgress.data.customerInfo?.fullName || 'cliente').toLowerCase().replace(/[^a-z0-9]+/gi, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating dual contract:', error);
      alert('Erro ao gerar contrato com 2 contratantes. Por favor, tente novamente.');
    } finally {
      setIsGeneratingDual(false);
    }
  };

  const handleDownloadWord = () => {
    try {
      const wordBlob = generateContractWord(formProgress.data);
      const url = window.URL.createObjectURL(wordBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato-${(formProgress.data.customerInfo?.fullName || 'cliente').toLowerCase().replace(/[^a-z0-9]+/gi, '-')}.doc`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Word document:', error);
      alert('Erro ao preparar o arquivo Word. Por favor, tente novamente.');
    }
  };

  const handleGeneratePowerOfAttorney = async () => {
    try {
      setIsGeneratingPowerOfAttorney(true);
      const powerOfAttorneyBlob = await generatePowerOfAttorney(formProgress.data);

      const url = window.URL.createObjectURL(powerOfAttorneyBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'procuracao-sistema-fotovoltaico.pdf');
      document.body.appendChild(link);
      link.click();

      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating power of attorney:', error);
      alert('Erro ao gerar procuração. Por favor, tente novamente.');
    } finally {
      setIsGeneratingPowerOfAttorney(false);
    }
  };

  const handleSaveContract = async () => {
    try {
      setIsSaving(true);

      await savePowerOfAttorney({
        full_name: formProgress.data.customerInfo?.fullName,
        cpf: formProgress.data.customerInfo?.cpf,
        rg: formProgress.data.customerInfo?.rg,
        issuing_authority: formProgress.data.customerInfo?.issuingAuthority,
        street: formProgress.data.installationLocation?.street,
        number: formProgress.data.installationLocation?.number,
        neighborhood: formProgress.data.installationLocation?.neighborhood,
        city: formProgress.data.installationLocation?.city,
        state: formProgress.data.installationLocation?.state
      });

      setShowSuccessMessage(true);

      setTimeout(() => {
        setFormProgress(prev => ({
          ...prev,
          isLocked: true
        }));
        setShowSuccessMessage(false);
      }, 2000);

    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Erro ao salvar contrato. Por favor, tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Revisão do Contrato</h2>
        {formProgress.isLocked && (
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
              <Lock className="w-4 h-4" />
              Contrato Bloqueado
            </span>
            <button
              onClick={() => setShowUnlockModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Unlock className="w-4 h-4" />
              Desbloquear para Editar
            </button>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        onConfirm={handleUnlockContract}
        title="Desbloquear Contrato"
        message="Tem certeza que deseja desbloquear o contrato para edicao? Isso permitira alterar todos os campos do formulario."
        confirmText="Sim, Desbloquear"
        cancelText="Cancelar"
      />

      <div className="space-y-6">
        {/* Customer Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2" />
            Informações do Cliente
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Nome</p>
              <p className="text-gray-900">{formProgress.data.customerInfo?.fullName}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Estado Civil</p>
              <p className="text-gray-900">
                {maritalStatusOptions.find(
                  status => status.value === formProgress.data.customerInfo?.maritalStatus
                )?.translation || formProgress.data.customerInfo?.maritalStatus}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">CPF</p>
              <p className="text-gray-900">{formProgress.data.customerInfo?.cpf}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">RG</p>
              <p className="text-gray-900">{formProgress.data.customerInfo?.rg}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Órgão Emissor</p>
              <p className="text-gray-900">{formProgress.data.customerInfo?.issuingAuthority}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Profissão</p>
              <p className="text-gray-900">{formProgress.data.customerInfo?.profession}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Nacionalidade</p>
              <p className="text-gray-900">{formProgress.data.customerInfo?.nationality}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="text-gray-900">{formProgress.data.customerInfo?.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Telefone</p>
              <p className="text-gray-900">{formProgress.data.customerInfo?.phone}</p>
            </div>
          </div>
        </div>

        {/* Installation Location */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            Local da Instalação
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Endereço</p>
              <p className="text-gray-900">
                {formProgress.data.installationLocation?.street}, {formProgress.data.installationLocation?.number}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Cidade/Estado</p>
              <p className="text-gray-900">
                {formProgress.data.installationLocation?.city}/{formProgress.data.installationLocation?.state}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">CEP</p>
              <p className="text-gray-900">{formProgress.data.installationLocation?.zipCode}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Código UC</p>
              <p className="text-gray-900">{formProgress.data.installationLocation?.utilityCode}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Bairro</p>
              <p className="text-gray-900">{formProgress.data.installationLocation?.neighborhood}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Concessionária</p>
              <p className="text-gray-900">{formProgress.data.installationLocation?.utilityCompany}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Tipo de Instalação</p>
              <p className="text-gray-900">{formProgress.data.installationLocation?.installationType}</p>
            </div>
          </div>
        </div>

        {/* Technical Configuration */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <Zap className="w-5 h-5 mr-2" />
            Configuração Técnica
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-700">Inversor Principal</h4>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <p className="text-sm font-medium text-gray-500">Fabricante</p>
                  <p className="text-gray-900">{formProgress.data.technicalConfig?.inverter1.brand}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Potência</p>
                  <p className="text-gray-900">{formProgress.data.technicalConfig?.inverter1.power} kW</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Quantidade</p>
                  <p className="text-gray-900">{formProgress.data.technicalConfig?.inverter1.quantity}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Garantia (anos)</p>
                  <p className="text-gray-900">{formProgress.data.technicalConfig?.inverter1.warrantyPeriod}</p>
                </div>
              </div>
            </div>
            {formProgress.data.technicalConfig?.inverter2 && (
              <div>
                <h4 className="font-medium text-gray-700 mt-4">Inversor Secundário</h4>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Fabricante</p>
                    <p className="text-gray-900">{formProgress.data.technicalConfig.inverter2.brand}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Potência</p>
                    <p className="text-gray-900">{formProgress.data.technicalConfig.inverter2.power} kW</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Quantidade</p>
                    <p className="text-gray-900">{formProgress.data.technicalConfig.inverter2.quantity}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Garantia (anos)</p>
                    <p className="text-gray-900">{formProgress.data.technicalConfig.inverter2.warrantyPeriod}</p>
                  </div>
                </div>
              </div>
            )}
            <div>
              <h4 className="font-medium text-gray-700">Módulos Solares</h4>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <p className="text-sm font-medium text-gray-500">Fabricante</p>
                  <p className="text-gray-900">{formProgress.data.technicalConfig?.solarModules.brand}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Quantidade</p>
                  <p className="text-gray-900">{formProgress.data.technicalConfig?.solarModules.quantity} unidades</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Potência</p>
                  <p className="text-gray-900">{formProgress.data.technicalConfig?.solarModules.power} W</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Dias para Instalação</p>
                  <p className="text-gray-900">{formProgress.data.technicalConfig?.installationDays} dias</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Terms */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Termos Financeiros
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Valor Total</p>
              <p className="text-gray-900">
                R$ {formProgress.data.financialTerms?.totalAmount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Parcelas</p>
              <div className="mt-2 space-y-2">
                {formProgress.data.financialTerms?.installments.map((installment, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>Parcela {index + 1} - {paymentMethods.find(m => m.value === installment.method)?.label}</span>
                    <div>
                      <span className="mr-4">R$ {installment.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      <span className="text-gray-500">Venc: {installment.dueDate.toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Second Contractor Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <button
            type="button"
            onClick={() => setShowDualContract(!showDualContract)}
            className="w-full flex items-center justify-between text-left"
          >
            <h3 className="text-lg font-semibold text-gray-700 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Segundo Contratante (Contrato com 2 pessoas)
            </h3>
            <span className="text-sm text-blue-600 font-medium">
              {showDualContract ? 'Ocultar' : 'Mostrar'}
            </span>
          </button>

          {showDualContract && (
            <div className="mt-4 space-y-4">
              <p className="text-sm text-gray-600">
                Preencha os dados do segundo contratante para gerar um contrato único com ambas as partes.
                O contrato já inclui cláusula de que todos os serviços de rede elétrica necessários para o funcionamento do kit solar estão inclusos.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
                  <input
                    type="text"
                    value={secondContractor.fullName}
                    onChange={(e) => setSecondContractor(prev => ({ ...prev, fullName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="IRIANA LOPES DE CASTRO FAIDIGA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
                  <input
                    type="text"
                    value={secondContractor.cpf}
                    onChange={(e) => setSecondContractor(prev => ({ ...prev, cpf: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="000.000.000-00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">RG *</label>
                  <input
                    type="text"
                    value={secondContractor.rg}
                    onChange={(e) => setSecondContractor(prev => ({ ...prev, rg: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="148209178"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Órgão Emissor</label>
                  <input
                    type="text"
                    value={secondContractor.issuingAuthority}
                    onChange={(e) => setSecondContractor(prev => ({ ...prev, issuingAuthority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="SESP/SP"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nacionalidade</label>
                  <input
                    type="text"
                    value={secondContractor.nationality}
                    onChange={(e) => setSecondContractor(prev => ({ ...prev, nationality: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="BRASILEIRA"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={secondContractor.sameAddress}
                      onChange={(e) => setSecondContractor(prev => ({ ...prev, sameAddress: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    Mesmo endereço do Contratante 1
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="text"
                    value={secondContractor.email || ''}
                    onChange={(e) => setSecondContractor(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="IRIANAFAIDIGA@YAHOO.COM.BR"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado Civil</label>
                  <select
                    value={secondContractor.maritalStatus || ''}
                    onChange={(e) => setSecondContractor(prev => ({ ...prev, maritalStatus: e.target.value as SecondContractor['maritalStatus'] }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selecione...</option>
                    <option value="Single">Solteiro(a)</option>
                    <option value="Married">Casado(a)</option>
                    <option value="Divorced">Divorciado(a)</option>
                    <option value="Widowed">Viúvo(a)</option>
                  </select>
                </div>
              </div>

              <button
                onClick={handleGenerateDualContract}
                disabled={isGeneratingDual || isSaving || showSuccessMessage}
                className="w-full px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-semibold"
              >
                {isGeneratingDual ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Gerando Contrato com 2 Contratantes...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5 mr-2" />
                    Gerar Contrato PDF com 2 Contratantes
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Success Message */}
        {showSuccessMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
            <p className="text-green-800 font-semibold">Contrato salvo com sucesso! Redirecionando...</p>
          </div>
        )}

        {/* Generate Documents Buttons */}
        <div className="flex flex-wrap gap-4 justify-between">
          <button
            type="button"
            onClick={() =>
              setFormProgress(prev => ({
                ...prev,
                currentStep: 'financial-terms'
              }))
            }
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center"
            disabled={isSaving || showSuccessMessage}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </button>

          <div className="flex flex-col md:flex-row gap-4 ml-auto">
            <button
              onClick={handleSincetClick}
              disabled={isAutomating || isSaving || showSuccessMessage}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isAutomating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Iniciando Fluxo...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {estadoLogin ? "Confirmar Login e Iniciar Fluxo" : "Sincet"}
                </>
              )}
            </button>

            <button
              onClick={handleGeneratePowerOfAttorney}
              disabled={isGeneratingPowerOfAttorney || isSaving || showSuccessMessage}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isGeneratingPowerOfAttorney ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando Procuração...
                </>
              ) : (
                <>
                  <FileSignature className="w-4 h-4 mr-2" />
                  Gerar Procuração
                </>
              )}
            </button>

            <button
              onClick={handleDownloadWord}
              disabled={isSaving || showSuccessMessage}
              className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Baixar em Word
            </button>

            <button
              onClick={handleGenerateDualContract}
              disabled={isGeneratingDual || isSaving || showSuccessMessage}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isGeneratingDual ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Users className="w-4 h-4 mr-2" />
                  Contrato 2 Contratantes
                </>
              )}
            </button>

            <button
              onClick={handleGenerateContract}
              disabled={isGeneratingContract || isSaving || showSuccessMessage}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isGeneratingContract ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando Documentos...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Gerar Contrato + Procuração
                </>
              )}
            </button>

            <button
              onClick={handleSaveContract}
              disabled={isSaving || showSuccessMessage}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando Contrato...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Contrato
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}